// List of available language codes based on locale folders
// This should match the folders in public/locales/
export const AVAILABLE_LANGUAGES = [
  'ae',
  'al',
  'ao',
  'ar',
  'at',
  'au',
  'bd',
  'be',
  'bg',
  'bh',
  'bo',
  'br',
  'bs',
  'by',
  'ca',
  'cd',
  'ch',
  'ci',
  'cl',
  'cm',
  'cn',
  'co',
  'cr',
  'cy',
  'cz',
  'de',
  'dk',
  'do',
  'dz',
  'ec',
  'ee',
  'eg',
  'en',
  'es',
  'et',
  'fi',
  'fr',
  'gb',
  'ge',
  'gh',
  'gr',
  'gt',
  'hk',
  'hn',
  'hr',
  'hu',
  'id',
  'ie',
  'il',
  'in',
  'iq',
  'is',
  'it',
  'jm',
  'jo',
  'jp',
  'ke',
  'kr',
  'kw',
  'kz',
  'lt',
  'lu',
  'lv',
  'ma',
  'md',
  'mk',
  'mt',
  'mx',
  'my',
  'na',
  'ne',
  'ng',
  'ni',
  'nl',
  'no',
  'nz',
  'om',
  'pa',
  'pe',
  'ph',
  'pk',
  'pl',
  'pr',
  'ps',
  'pt',
  'py',
  'qa',
  'ro',
  'rs',
  'ru',
  'sa',
  'se',
  'sg',
  'si',
  'sk',
  'sn',
  'sv',
  'th',
  'tn',
  'tr',
  'tw',
  'tz',
  'ua',
  'ug',
  'us',
  'uy',
  'vn',
  'za',
] as const;

export type LanguageCode = (typeof AVAILABLE_LANGUAGES)[number];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

// Language display names mapping
export const LANGUAGE_NAMES: Record<LanguageCode, string> = {
  ae: 'العربية (AE)',
  al: 'Shqip',
  ao: 'Português (AO)',
  ar: 'العربية',
  at: 'Deutsch (AT)',
  au: 'English (AU)',
  bd: 'বাংলা',
  be: 'Nederlands (BE)',
  bg: 'Български',
  bh: 'العربية (BH)',
  bo: 'Español (BO)',
  br: 'Português (BR)',
  bs: 'Bosanski',
  by: 'Беларуская',
  ca: 'English (CA)',
  cd: 'Français (CD)',
  ch: 'Deutsch (CH)',
  ci: 'Français (CI)',
  cl: 'Español (CL)',
  cm: 'Français (CM)',
  cn: '中文',
  co: 'Español (CO)',
  cr: 'Español (CR)',
  cy: 'Ελληνικά',
  cz: 'Čeština',
  de: 'Deutsch',
  dk: 'Dansk',
  do: 'Español (DO)',
  dz: 'العربية (DZ)',
  ec: 'Español (EC)',
  ee: 'Eesti',
  eg: 'العربية (EG)',
  en: 'English',
  es: 'Español',
  et: 'አማርኛ',
  fi: 'Suomi',
  fr: 'Français',
  gb: 'English (GB)',
  ge: 'ქართული',
  gh: 'English (GH)',
  gr: 'Ελληνικά',
  gt: 'Español (GT)',
  hk: '中文 (HK)',
  hn: 'Español (HN)',
  hr: 'Hrvatski',
  hu: 'Magyar',
  id: 'Bahasa Indonesia',
  ie: 'English (IE)',
  il: 'עברית',
  in: 'हिन्दी',
  iq: 'العربية (IQ)',
  is: 'Íslenska',
  it: 'Italiano',
  jm: 'English (JM)',
  jo: 'العربية (JO)',
  jp: '日本語',
  ke: 'Kiswahili',
  kr: '한국어',
  kw: 'العربية (KW)',
  kz: 'Қазақ',
  lt: 'Lietuvių',
  lu: 'Lëtzebuergesch',
  lv: 'Latviešu',
  ma: 'العربية (MA)',
  md: 'Română',
  mk: 'Македонски',
  mt: 'Malti',
  mx: 'Español (MX)',
  my: 'Bahasa Melayu',
  na: 'English (NA)',
  ne: 'Français (NE)',
  ng: 'English (NG)',
  ni: 'Español (NI)',
  nl: 'Nederlands',
  no: 'Norsk',
  nz: 'English (NZ)',
  om: 'العربية (OM)',
  pa: 'Español (PA)',
  pe: 'Español (PE)',
  ph: 'English (PH)',
  pk: 'اردو',
  pl: 'Polski',
  pr: 'Español (PR)',
  ps: 'العربية (PS)',
  pt: 'Português',
  py: 'Español (PY)',
  qa: 'العربية (QA)',
  ro: 'Română',
  rs: 'Српски',
  ru: 'Русский',
  sa: 'العربية (SA)',
  se: 'Svenska',
  sg: 'English (SG)',
  si: 'Slovenščina',
  sk: 'Slovenčina',
  sn: 'Français (SN)',
  sv: 'Svenska',
  th: 'ไทย',
  tn: 'العربية (TN)',
  tr: 'Türkçe',
  tw: '中文 (TW)',
  tz: 'Kiswahili',
  ua: 'Українська',
  ug: 'English (UG)',
  us: 'English (US)',
  uy: 'Español (UY)',
  vn: 'Tiếng Việt',
  za: 'English (ZA)',
};

/**
 * Check if a language code is valid
 */
export function isValidLanguageCode(code: string): code is LanguageCode {
  return AVAILABLE_LANGUAGES.includes(code as LanguageCode);
}

/**
 * Get language code from URL path
 */
export function getLanguageFromPath(pathname: string): LanguageCode | null {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();

  if (firstSegment && isValidLanguageCode(firstSegment)) {
    return firstSegment;
  }

  return null;
}

/**
 * Remove language prefix from path
 */
export function removeLanguageFromPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();

  if (firstSegment && isValidLanguageCode(firstSegment)) {
    return '/' + segments.slice(1).join('/');
  }

  return pathname;
}

/**
 * Add language prefix to path
 */
export function addLanguageToPath(pathname: string, language: LanguageCode): string {
  const pathWithoutLang = removeLanguageFromPath(pathname);
  const cleanPath = pathWithoutLang === '/' ? '' : pathWithoutLang;
  return `/${language}${cleanPath}`;
}
